import React from 'react';
import t from 'prop-types';
import { StaticSelect } from '@kineticdata/react';
import { generateTypeaheadComponents } from '@kineticdata/bundle-common';
import { fromJS } from 'immutable';
import IconSet from '../line-awesome-icons.json';

const SuggestionContent = ({ suggestion }) => (
  <div className="large">
    <span className={`fa-fw fa-lg mr-3 ${suggestion.get('value')}`} />
    <span>{suggestion.get('label') || suggestion.get('value')}</span>
  </div>
);

export const generateTypeaheadSelection = ({ id, placeholder }) => ({
  selection,
  disabled,
  edit,
  focusRef,
  remove,
}) => (
  <div className="input-group selection">
    {selection && (
      <div className="input-group-prepend">
        <div className="input-group-text">
          <span className={`fa-fw fa-lg ${selection.get('value')}`} />
        </div>
      </div>
    )}
    <input
      id={id}
      className="form-control"
      type="text"
      value={selection ? selection.get('label') || selection.get('value') : ''}
      onChange={() => {}}
      onFocus={e => e.target.click()}
      onClick={edit}
      disabled={disabled}
      placeholder={placeholder}
    />
    {selection &&
      !disabled && (
        <div className="input-group-append">
          <button
            className="btn btn-sm btn-clear"
            onClick={remove}
            type="button"
          >
            <span className="sr-only">Remove</span>
            <i className="fa fa-fw fa-times" />
          </button>
        </div>
      )}
  </div>
);

export class IconTypeahead extends React.Component {
  constructor(props) {
    super(props);
    this.components = generateTypeaheadComponents({
      ...props,
      labelProp: 'label',
      valueProp: 'value',
      components: {
        SuggestionContent,
        Selection: generateTypeaheadSelection({
          id: props.id,
          placeholder: props.placeholder,
        }),
        ...(props.components || {}),
      },
    });
    this.options = IconSet.flatMap(icon =>
      [icon.las && 'fa', icon.lar && 'far', icon.lab && 'fab']
        .filter(Boolean)
        .map(prefix => ({
          value: `${prefix} fa-${icon.value}`,
          label: icon.value,
          searchterms: icon.searchterms.join('|'),
        })),
    );
  }

  handleChange = val => {
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(val ? val.get('value') : '');
    }
  };

  render() {
    return (
      <StaticSelect
        {...this.props}
        components={this.components}
        value={fromJS(
          !!this.props.value
            ? this.options.find(o => o.value === this.props.value)
            : null,
        )}
        onChange={this.handleChange}
        search={fromJS({
          fields: [
            { name: 'label', operator: 'matches' },
            { name: 'searchterms', operator: 'matches' },
          ],
          limit: (this.props.search && this.props.search.limit) || 25,
        })}
        options={fromJS(this.options)}
      />
    );
  }
}

IconTypeahead.propTypes = {
  // The string of the icon classes.
  value: t.string,
  // Function triggered when a new option is selected that takes the selected
  // option (as a string) and sets the value
  onChange: t.func.isRequired,
  // Function triggered when the input is focused.
  onFocus: t.func,
  // Function triggered when the input is blurred.
  onBlur: t.func,
  // Value to set as the HTML id attribute on the input.
  id: t.string,
  // Value to set as the placeholder of the input.
  placeholder: t.string,
  // Flag for disabling the typeahead input.
  disabled: t.bool,
  // Search function or configuration to be used when searching for data. If a
  // function is provided, the function will receive the options and typed in
  // value and should return a list of matching options.
  search: t.shape({
    // The max number of options to display at once. Showing too many results
    // leads to performance issues. Defaults to 25.
    limit: t.number,
  }),
  // The minimum number of characters that need to be entered before the bridge
  // query is triggered. Defaults to 0.
  minSearchLength: t.number,
  // Overrides for the status messages that show up in the typeahead.
  messages: t.shape({
    // Not enough characters have been typed in to trigger a search.
    short: t.string,
    // No results found and allowNew is false.
    empty: t.string,
    // No results found and allowNew is a function that returned false. Use to
    // tell the user what valid, custom options they can type in.
    custom: t.string,
    // Searching in progress.
    pending: t.string,
    // Too many results to show all.
    more: t.string,
  }),
  // Overrides for the components used to render the typeahead.
  components: t.shape({
    // Renders the input element.
    Input: t.func,
    // Renders the value (or each value if multiple are allowed).
    Selection: t.func,
    // Wrapper for the selection and input elements.
    SelectionsContainer: t.func,
    // Renders the status messages inside the suggestions container, before the
    // suggestions.
    Status: t.func,
    // Renders a single suggestion.
    Suggestion: t.func,
    // Renders the content of a suggestion. Only used if the Suggestion
    // component is not overriden.
    SuggestionContent: t.func,
    // Wrapper for the suggestions and status messages.
    SuggestionsContainer: t.func,
  }),
};
